<template>
  <div
    class="c-graph c-graph--stack"
    :class="{
      'is-error': !data.length,
      'is-loading': isLoading || isDrawing
    }"
    ref="canvas"
  >
    <Loading v-if="isLoading || isDrawing" transparent />

    <template v-if="!isLoading && !data.length">
      <Error transparent>
        <template slot="title">
          コンテンツがありません。
        </template>
        <p>「検索順位チェック」に追加したキーワードのみが表示されます。</p>
      </Error>
    </template>

    <svg class="c-graph__svg" ref="svg">
      <defs ref="defs">
        <GraphMask id="stack" :size="size" />
      </defs>
      <g class="c-graph-bg" clip-path="url(#clipPath--stack)">
        <GraphBg
          :data="y.bg"
          direction="y"
          :scale="d3.y"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gBgY"
        />
      </g>
      <g class="c-graph-stack" clip-path="url(#clipPath--stack)" ref="gStack">
        <g ref="gStackMask"></g>
        <g ref="gStackBar"></g>
        <g ref="gStackLabel"></g>
      </g>
      <GraphFrame :size="size" hasLine />
      <g
        class="c-graph-pointer"
        clip-path="url(#clipPath--stack)"
        ref="gPointer"
      >
        <rect
          x="0"
          y="0"
          :height="size.canvasH"
          :width="size.canvasW"
          class="c-graph-pointer__area"
        />
      </g>
      <g class="c-graph-axis">
        <GraphAxis
          :data="x.axis"
          position="bottom"
          :scale="d3.x"
          :scaleType="x.scaleType"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gAxisX"
        />
        <GraphAxis
          v-show="period"
          :data="y.axis"
          position="left"
          :scale="d3.y"
          :scaleType="y.scaleType"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gAxisY"
        />
      </g>
    </svg>

    <GraphDashboardTooltip
      :position="{ x: tooltip.x, y: tooltip.y }"
      :size="size"
      :isVisible="tooltip.isVisible && data.length > 0 ? true : false"
    >
      <ul v-if="data.length" class="c-graph-tooltip-list--summary">
        <li
          v-if="tooltip.data.ctrHigh"
          class="c-graph-tooltip-list--summary__item"
        >
          <span
            class="c-graph-tooltip-list--summary__key c-graph-tooltip-list--summary__key--ctrHigh"
          >
            1~5位(クリック率が高い)
          </span>
          <span class="c-graph-tooltip-list--summary__value">
            {{ tooltip.data.ctrHigh.contentsCount }}件（{{ ctrHighPercent }}%）
          </span>
        </li>
        <li
          v-if="tooltip.data.ctrLow"
          class="c-graph-tooltip-list--summary__item"
        >
          <span
            class="c-graph-tooltip-list--summary__key c-graph-tooltip-list--summary__key--ctrLow"
          >
            1~5位(クリック率が低い)
          </span>
          <span class="c-graph-tooltip-list--summary__value">
            {{ tooltip.data.ctrLow.contentsCount }}件（{{ ctrLowPercent }}%）
          </span>
        </li>
        <li
          v-if="tooltip.data.rankMiddle"
          class="c-graph-tooltip-list--summary__item"
        >
          <span
            class="c-graph-tooltip-list--summary__key c-graph-tooltip-list--summary__key--rankMiddle"
          >
            6~50位
          </span>
          <span class="c-graph-tooltip-list--summary__value">
            {{ tooltip.data.rankMiddle.contentsCount }}件（{{
              rankMiddlePercent
            }}%）
          </span>
        </li>
        <li
          v-if="tooltip.data.rankLow"
          class="c-graph-tooltip-list--summary__item"
        >
          <span
            class="c-graph-tooltip-list--summary__key c-graph-tooltip-list--summary__key--rankLow"
          >
            51位以下(データなしを含む)&nbsp;
          </span>
          <span class="c-graph-tooltip-list--summary__value">
            {{ tooltip.data.rankLow.contentsCount }}件（{{ rankLowPercent }}%）
          </span>
        </li>
      </ul>
    </GraphDashboardTooltip>
  </div>
</template>

<script>
import Error from '@/components/Error';
import GraphAxis from '@/components/GraphAxis';
import GraphBg from '@/components/GraphBg';
import GraphFrame from '@/components/GraphFrame';
import GraphMask from '@/components/GraphMask';
import Loading from '@/components/Loading';
import GraphDashboardTooltip from '@/components/dashboard/GraphDashboardTooltip';

import utils from '@/utils';
import * as d3 from 'd3';

export default {
  props: ['data', 'period', 'isLoading'],

  components: {
    Error,
    GraphAxis,
    GraphBg,
    GraphFrame,
    GraphMask,
    Loading,
    GraphDashboardTooltip
  },

  data() {
    return {
      d3: {
        stack: () => {},
        x: () => {},
        y: () => {}
      },
      el: {
        canvas: '',
        svg: '',
        gDefs: '',
        gStack: '',
        gStackMask: '',
        gStackBar: '',
        gStackLabel: '',
        gMarker: '',
        gPointer: ''
      },
      path: {
        data: [],
        resetData: []
      },
      size: {
        axisXH: 22,
        axisYW: {
          left: 20,
          right: 0
        },
        barW: 24,
        canvasH: 0,
        canvasW: 0,
        frameW: 1,
        graphH: 0,
        graphW: 0,
        graph: {
          bottom: 0,
          left: 0,
          right: 0,
          top: 0
        },
        labelH: 18,
        offset: {
          bottom: 0,
          left: 0,
          right: 0,
          top: 0
        },
        windowW: window.innerWidth
      },
      tooltip: {
        data: {},
        x: 0,
        y: 0,
        isVisible: false
      },
      transition: {
        duration: 500,
        ease: d3.easeQuad
      },
      x: {
        axis: [],
        bg: [],
        domain: [],
        scaleType: 'timeMonth'
      },
      y: {
        axis: [],
        bg: [],
        domain: [],
        scaleType: 'number'
      },
      isDrawed: false,
      isDrawing: false
    };
  },

  computed: {
    ctrHighPercent() {
      return this.percent(this.tooltip.data.ctrHigh.ratio);
    },
    ctrLowPercent() {
      return this.percent(this.tooltip.data.ctrLow.ratio);
    },
    rankMiddlePercent() {
      return this.percent(this.tooltip.data.rankMiddle.ratio);
    },
    rankLowPercent() {
      return this.percent(this.tooltip.data.rankLow.ratio);
    }
  },

  watch: {
    data: {
      handler() {
        this.update();
      },
      deep: true
    },

    'size.windowW'() {
      this.resize();
    }
  },

  mounted() {
    this.setEl();
    this.setSize();
    this.update();
    this.setEvent();
  },

  methods: {
    update() {
      if (this.isLoading) {
        return;
      }
      this.isDrawing = true;
      this.hideTooltip();
      this.setData();
      this.setScale();
      this.draw();
      this.isDrawed = true;
      this.isDrawing = false;
    },

    setEl() {
      this.el.canvas = this.$refs.canvas;
      this.el.svg = d3.select(this.$refs.svg);
      this.el.gDefs = d3.select(this.$refs.defs);
      this.el.gStack = d3.select(this.$refs.gStack);
      this.el.gStackMask = d3.select(this.$refs.gStackMask);
      this.el.gStackBar = d3.select(this.$refs.gStackBar);
      this.el.gStackLabel = d3.select(this.$refs.gStackLabel);
      this.el.gMarker = d3.select(this.$refs.gMarker);
      this.el.gPointer = d3.select(this.$refs.gPointer);
    },

    setSize() {
      this.size.canvasH = this.el.canvas.clientHeight;
      this.size.canvasW = this.el.canvas.clientWidth;

      this.size.graphH =
        this.size.canvasH -
        this.size.axisXH -
        this.size.frameW * 2 -
        this.size.offset.top -
        this.size.offset.bottom;

      this.size.graphW =
        this.size.canvasW -
        this.size.axisYW.left -
        this.size.axisYW.right -
        this.size.frameW * 2 -
        this.size.offset.left -
        this.size.offset.right;

      this.size.graph.bottom =
        this.size.canvasH -
        this.size.axisXH -
        this.size.frameW -
        this.size.offset.bottom;

      this.size.graph.left =
        this.size.offset.left + this.size.axisYW.left + this.size.frameW;

      this.size.graph.right =
        this.size.canvasW -
        this.size.axisYW.right -
        this.size.frameW -
        this.size.offset.right;

      this.size.graph.top = this.size.offset.top + this.size.frameW;
    },

    setEvent() {
      window.addEventListener(
        'resize',
        () => {
          this.size.windowW = window.innerWidth;
        },
        false
      );

      this.el.gPointer
        .on('mousemove', this.mousemovePointer)
        .on('mouseleave', this.hideTooltip);
    },

    setData() {
      var dataFormat = [];
      for (let i = 0; i < this.data.length; i++) {
        dataFormat[i] = {
          ctrHigh: this.data[i].ctrHigh.contentsCount,
          ctrLow: this.data[i].ctrLow.contentsCount,
          rankMiddle: this.data[i].rankMiddle.contentsCount,
          rankLow: this.data[i].rankLow.contentsCount,
          yearMonth: this.data[i].yearMonth
        };
      }

      this.path.data = d3
        .stack()
        .keys(['rankLow', 'rankMiddle', 'ctrLow', 'ctrHigh'])(dataFormat);

      this.x.domain = this.getDomainX();
      this.x.bg = this.getBgX();
      this.x.axis = this.getAxisX();

      this.y.axis = this.getAxisY();
      this.y.bg = this.getBgY();
      this.y.domain = this.getDomainY();
    },

    getDomainX() {
      let array = [
        utils.getStartDate(this.period[0], 'month'),
        utils.getStartDate(this.period[1], 'month')
      ];
      return array;
    },

    getBgX() {
      let array = d3[this.x.scaleType].range(
        this.x.domain[0],
        this.x.domain[1]
      );
      if (!array.length) {
        array = [this.x.domain[0]];
      }
      return array;
    },

    getAxisX() {
      let max = this.x.bg.length;
      return Math.min(max, 12);
    },

    getAxisY() {
      let max = d3.max(
        this.data.map(item => {
          return (
            item.ctrHigh.contentsCount +
            item.ctrLow.contentsCount +
            item.rankMiddle.contentsCount +
            item.rankLow.contentsCount
          );
        })
      );
      return utils.getTickValues(5, max + 1);
    },

    getBgY() {
      let array = d3.range(
        this.y.axis[1] / 2,
        this.y.axis[this.y.axis.length - 1],
        this.y.axis[1] / 2
      );
      return array;
    },

    getDomainY() {
      return [0, this.y.axis[this.y.axis.length - 1]];
    },

    setScale() {
      this.d3.x = d3
        .scaleTime()
        .domain(this.x.domain)
        .rangeRound([this.size.graph.left + 80, this.size.graph.right - 80]);

      this.d3.y = d3
        .scaleLinear()
        .domain(this.y.domain)
        .rangeRound([this.size.graph.bottom, this.size.graph.top]);
    },

    draw() {
      this.drawPath();
      this.$refs.gAxisX.draw();
      this.$refs.gAxisY.draw();
      this.$refs.gBgY.draw();
      this.drawPointer();
    },

    attrStackMask(selection) {
      selection
        .attr('x', (d, i) => {
          return (
            this.d3.x(utils.getStartDate(d.data.yearMonth, 'month')) -
            this.size.barW / 2
          );
        })
        .attr('y', (d, i) => {
          let sum =
            d.data.ctrHigh + d.data.ctrLow + d.data.rankMiddle + d.data.rankLow;
          return this.d3.y(sum);
        })
        .attr('height', this.size.canvasH)
        .attr('width', this.size.barW)
        .attr('rx', 4)
        .attr('ry', 4);
    },

    attrStackBar(selection) {
      selection
        .attr('x', (d, i) => {
          return (
            this.d3.x(utils.getStartDate(d.data.yearMonth, 'month')) -
            this.size.barW / 2
          );
        })
        .attr('y', (d, i) => {
          return this.d3.y(d[1]);
        })
        .attr('height', (d, i) => {
          return this.d3.y(d[0]) - this.d3.y(d[1]);
        })
        .attr('width', this.size.barW);
    },

    drawPath() {
      let transition = d3
        .transition()
        .duration(this.transition.duration)
        .ease(this.transition.ease);

      this.el.gStackMask
        .selectAll('clipPath')
        .data(this.path.data[1])
        .join(
          enter => {
            return enter
              .append('clipPath')
              .attr('id', (d, i) => {
                return 'stackClipPath' + i;
              })
              .call(enter => {
                enter.append('rect').call(this.attrStackMask);
              });
          },
          update => {
            return update
              .select('rect')
              .interrupt()
              .transition(transition)
              .call(this.attrStackMask);
          },
          exit => {
            return exit.remove();
          }
        );

      this.el.gStackBar
        .selectAll('g')
        .data(this.path.data)
        .join(
          enter => {
            return enter.append('g').attr('class', (d, i) => {
              return 'c-graph-stack__bar c-graph-stack__bar--' + d.key;
            });
          },
          update => {
            return update;
          },
          exit => {
            return exit.remove();
          }
        )
        .each((d, index, nodes) => {
          d3.select(nodes[index])
            .selectAll('rect')
            .data(d)
            .join(
              enter => {
                return enter
                  .append('rect')
                  .attr('clip-path', (d, i) => {
                    return 'url(#stackClipPath' + i + ')';
                  })
                  .call(this.attrStackBar);
              },
              update => {
                return update
                  .interrupt()
                  .transition(transition)
                  .call(this.attrStackBar);
              },
              exit => {
                return exit.remove();
              }
            );
        });
    },

    //ポインタ
    attrPointer(selection) {
      selection.attr('transform', (d, i) => {
        let x = this.d3.x(utils.getStartDate(d.yearMonth, 'month'));

        return `translate(${x}, 0)`;
      });
    },

    drawPointer() {
      this.el.gPointer
        .selectAll('g')
        .data(this.data)
        .join(
          enter => {
            return enter
              .append('g')
              .call(this.attrPointer)
              .call(enter => {
                enter
                  .append('line')
                  .attr('class', 'c-graph-pointer__line')
                  .attr('x1', 0)
                  .attr('x2', 0)
                  .attr('y1', 0)
                  .attr('y2', this.size.canvasH - this.size.axisXH);
              });
          },
          update => {
            return update.call(this.attrPointer);
          },
          exit => {
            return exit.remove();
          }
        );
    },

    mousemovePointer() {
      if (!this.isDrawed) {
        return;
      }
      let isHovered = false;

      this.el.gPointer
        .selectAll('g')
        .classed('is-hover', false)
        .each((d, i, nodes) => {
          if (isHovered) {
            return;
          }

          const x1 = this.d3.x(utils.getStartDate(d.yearMonth, 'month'));
          let start =
            this.d3.x(utils.getStartDate(d.yearMonth, 'month')) -
            this.size.barW / 2;
          let end =
            this.d3.x(utils.getStartDate(d.yearMonth, 'month')) +
            this.size.barW / 2;

          if (d3.event.offsetX >= start && d3.event.offsetX <= end) {
            isHovered = true;
            d3.select(nodes[i]).classed('is-hover', true);
            this.showTooltip(d, x1, d3.event.offsetY);
          }
        });

      if (!isHovered) {
        this.hideTooltip();
      }
    },

    //ツールチップ
    showTooltip(d, x, y) {
      const data = Object.assign({}, d);

      this.tooltip.data = data;
      this.tooltip.x = x;
      this.tooltip.y = y;
      this.tooltip.isVisible = true;
    },

    hideTooltip() {
      this.tooltip.isVisible = false;
      this.el.gPointer.selectAll('g').classed('is-hover', false);
    },

    resize() {
      this.setSize();

      if (!this.isDrawed) {
        return;
      }
      this.setData();
      this.setScale();

      this.el.gStackMask
        .selectAll('clipPath')
        .select('rect')
        .call(this.attrStackMask);

      this.el.gStackBar
        .selectAll('g')
        .selectAll('rect')
        .call(this.attrStackBar);

      this.el.gPointer.selectAll('g').call(this.attrPointer);

      this.$refs.gAxisX.resize();
      this.$refs.gBgY.resize();
    },

    // 小数点以下の丸め誤差が生じるので%表記に変換する際に小数点以下を切り捨てて丸める
    percent(ratio) {
      return this.$options.filters.roundFloatingPointNumber(ratio * 100, 0);
    }
  }
};
</script>
